import { useEffect, useState } from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useWS } from "@/contexts/WebSocketContext";
import {
  ChartBar,
  Stack,
  FileText,
  Terminal,
  Bell,
  ListChecks,
  ShieldCheck,
  SignOut,
  Pulse,
  List,
  X,
  Key,
} from "@phosphor-icons/react";
import BottomDock, { NAV_ITEMS } from "@/components/layout/BottomDock";
import ChangePasswordModal from "@/components/ChangePasswordModal";
import ThemeToggle from "@/components/layout/ThemeToggle";

const DOCK_ICONS = {
  "/": ChartBar,
  "/accounts": Stack,
  "/reports": FileText,
  "/commands": Terminal,
  "/notifications": Bell,
  "/audit": ListChecks,
  "/admin": ShieldCheck,
  default: ChartBar,
};

function SidebarContent({ user, status, onNavigate, onLogout, onChangePassword, className = "" }) {
  const navItems = NAV_ITEMS.filter((n) => !n.roles || n.roles.includes(user?.role));

  return (
    <div className={`flex flex-col h-full ${className}`.trim()}>
      <div className="p-4 sm:p-6 border-b border-app-border-strong layout-sidebar shrink-0">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-app-accent flex items-center justify-center font-display font-black text-sm text-white shrink-0">M</div>
          <div className="min-w-0">
            <div className="font-display font-black text-sm tracking-tight truncate">MT5 COMMAND</div>
            <div className="font-mono-d text-[10px] text-app-accent tracking-widest font-semibold uppercase">HUB v2.12-HEDGE</div>
          </div>
        </div>
      </div>

      <nav className="flex-1 min-h-0 py-3 overflow-y-auto" aria-label="Main navigation">
        {navItems.map((n) => {
          const Icon = DOCK_ICONS[n.to] || DOCK_ICONS.default;
          return (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={n.navTestid}
              onClick={onNavigate}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 sm:px-6 py-3.5 text-sm transition-colors border-l-[3px] min-h-[44px] ${
                  isActive
                    ? "nav-item-active"
                    : "border-transparent text-app-muted hover:text-app-text hover:bg-app-hover font-medium"
                }`
              }
            >
              <Icon size={18} weight="duotone" className="shrink-0" />
              <span className="font-medium">{n.label}</span>
            </NavLink>
          );
        })}
      </nav>

      <div className="p-4 border-t border-app-border shrink-0">
        <div className="flex items-center gap-2 mb-3">
          <Pulse size={14} className={status === "connected" ? "text-[#10B981]" : "text-[#FF3B30]"} />
          <span className="font-mono-d text-[10px] tracking-widest uppercase text-app-subtle" data-testid="ws-status">
            {status === "connected" ? "LIVE" : status === "error" ? "ERROR" : "OFFLINE"}
          </span>
          {status === "connected" && <span className="w-1.5 h-1.5 bg-[#10B981] rounded-full pulse-dot" />}
        </div>
        <div className="pb-3 border-b border-app-border mb-3">
          <div className="font-display font-bold text-sm truncate" data-testid="user-name">{user?.name}</div>
          <div className="font-mono-d text-[10px] text-app-muted uppercase tracking-widest">{user?.role}</div>
        </div>
        <ThemeToggle className="mb-2" />
        <button
          type="button"
          onClick={onChangePassword}
          data-testid="change-password-btn"
          className="touch-target w-full flex items-center justify-center gap-2 text-xs uppercase tracking-widest font-semibold py-2.5 border border-app-border hover:bg-app-hover transition mb-2"
        >
          <Key size={14} /> Change password
        </button>
        <button
          type="button"
          onClick={onLogout}
          data-testid="logout-btn"
          className="touch-target w-full flex items-center justify-center gap-2 text-xs uppercase tracking-widest font-semibold py-2.5 border border-app-border hover:bg-app-hover transition"
        >
          <SignOut size={14} /> Sign out
        </button>
      </div>
    </div>
  );
}

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const { status } = useWS();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [passwordOpen, setPasswordOpen] = useState(false);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [menuOpen]);

  const handleLogout = async () => {
    setMenuOpen(false);
    await logout();
    navigate("/login");
  };

  const handleChangePassword = () => {
    setMenuOpen(false);
    setPasswordOpen(true);
  };

  const isCommandsPage = location.pathname === "/commands";

  return (
    <div className="min-h-screen bg-app-bg text-app-text overflow-x-hidden">
      <aside
        className="hidden lg:fixed lg:inset-y-0 lg:left-0 lg:z-30 lg:flex lg:w-64 lg:flex-col lg:overflow-hidden border-r border-app-border-strong layout-sidebar"
        data-testid="sidebar"
      >
        <SidebarContent user={user} status={status} onLogout={handleLogout} onChangePassword={handleChangePassword} />
      </aside>

      <div
        className={
          isCommandsPage
            ? "lg:ml-64 lg:h-[100dvh] lg:max-h-[100dvh] lg:flex lg:flex-col lg:overflow-hidden"
            : "lg:ml-64"
        }
      >
      <header className="lg:hidden sticky top-0 z-40 flex items-center justify-between gap-2 px-3 py-3 border-b border-app-border-strong layout-sidebar backdrop-blur-sm">
        <button
          type="button"
          onClick={() => setMenuOpen(true)}
          className="touch-target inline-flex items-center justify-center border border-app-border hover:bg-app-surface transition-colors"
          aria-label="Open menu — account & sign out"
          aria-expanded={menuOpen}
        >
          <List size={20} weight="bold" />
        </button>
        <div className="flex items-center gap-2 min-w-0 flex-1 justify-center">
          <div className="w-7 h-7 bg-app-accent flex items-center justify-center font-display font-black text-xs text-white shrink-0">M</div>
          <span className="font-display font-black text-sm tracking-tight truncate">MT5 COMMAND HUB v2.12-HEDGE</span>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <ThemeToggle compact />
          <Pulse size={14} className={status === "connected" ? "text-[#10B981]" : "text-[#FF3B30]"} />
          {status === "connected" && <span className="w-1.5 h-1.5 bg-[#10B981] rounded-full pulse-dot" />}
        </div>
      </header>

      {menuOpen && (
        <div className="lg:hidden fixed inset-0 z-50" role="dialog" aria-modal="true" aria-label="Navigation menu">
          <button
            type="button"
            className="absolute inset-0 bg-black/70 backdrop-blur-[2px]"
            onClick={() => setMenuOpen(false)}
            aria-label="Close navigation menu"
          />
          <aside className="absolute inset-y-0 left-0 w-[min(100vw-3rem,20rem)] max-w-full layout-sidebar border-r border-app-border-strong shadow-2xl animate-slide-in-left flex flex-col bottom-dock-offset">
            <div className="flex items-center justify-between px-4 py-3 border-b border-app-border">
              <span className="font-mono-d text-[10px] uppercase tracking-widest text-app-subtle">Menu</span>
              <button
                type="button"
                onClick={() => setMenuOpen(false)}
                className="touch-target inline-flex items-center justify-center border border-app-border hover:bg-app-surface"
                aria-label="Close menu"
              >
                <X size={18} weight="bold" />
              </button>
            </div>
            <SidebarContent
              user={user}
              status={status}
              onNavigate={() => setMenuOpen(false)}
              onLogout={handleLogout}
              onChangePassword={handleChangePassword}
              className="flex-1 min-h-0"
            />
          </aside>
        </div>
      )}

      <main
        className={
          isCommandsPage
            ? "min-w-0 overflow-x-hidden grain-bg mobile-main-with-dock lg:flex-1 lg:min-h-0 lg:flex lg:flex-col lg:overflow-hidden"
            : "min-w-0 overflow-x-hidden grain-bg mobile-main-with-dock"
        }
      >
        <Outlet />
      </main>

      <BottomDock user={user} icons={DOCK_ICONS} />
      <ChangePasswordModal open={passwordOpen} onClose={() => setPasswordOpen(false)} />
      </div>
    </div>
  );
}
